import {useEffect, useMemo, useRef, useState} from 'react';

import {type Line} from '../../util/plotHelpers/types';
import {type RunsLinePlotConfig} from './types';

const REGEX_METRIC_EMPTY_DATA_LOADING_GRACE_MS = 15000;

type LineDataShape = Pick<Line, 'data' | 'nanPoints'>;

function lineHasRenderableData(line: LineDataShape): boolean {
  return line.data.length > 0 || (line.nanPoints?.length ?? 0) > 0;
}

export function linesHaveRenderableData(lines: LineDataShape[]): boolean {
  return lines.some(lineHasRenderableData);
}

function getRegexMetricDataKey(config: RunsLinePlotConfig): string | null {
  if (
    !config.useMetricRegex ||
    !config.metricRegex ||
    config.metrics == null ||
    config.metrics.length === 0
  ) {
    return null;
  }

  return [config.metricRegex, ...config.metrics].join('\0');
}

export function useRegexMetricSettlingLoading(
  config: RunsLinePlotConfig,
  lines: LineDataShape[],
  graceMs = REGEX_METRIC_EMPTY_DATA_LOADING_GRACE_MS
): boolean {
  const dataKey = useMemo(() => getRegexMetricDataKey(config), [config]);
  const hasRenderableData = useMemo(
    () => linesHaveRenderableData(lines),
    [lines]
  );
  const previousDataKey = useRef(dataKey);
  const isDataKeyChanging =
    // oxlint-disable-next-line react/refs
    dataKey != null && previousDataKey.current !== dataKey;
  const [settlingDataKey, setSettlingDataKey] = useState<string | null>(null);

  useEffect(() => {
    if (dataKey == null) {
      previousDataKey.current = null;
      setSettlingDataKey(null);
      return;
    }

    if (previousDataKey.current === dataKey) {
      return;
    }

    // Regex metric edits update the selected metric names before sampled
    // history has necessarily arrived for those metrics. During that gap the
    // plot can have valid matched metric names but empty line data, which makes
    // the normal graph error path claim "no data" even though the query is
    // still settling. Treat only the immediate post-edit window as loading so
    // users see progress feedback; the timeout still lets genuine empty results
    // surface instead of hiding them forever.
    previousDataKey.current = dataKey;
    setSettlingDataKey(dataKey);

    const timeout = setTimeout(() => {
      setSettlingDataKey(currentKey =>
        currentKey === dataKey ? null : currentKey
      );
    }, graceMs);

    return () => clearTimeout(timeout);
  }, [dataKey, graceMs]);

  // Keep `settlingDataKey` until its timeout instead of clearing it as soon as
  // any renderable lines exist. `usePanelRunsData` can keep stale lines from
  // the previous regex visible while the next query is in flight; if those old
  // lines disappear before fresh history arrives, the current regex still
  // needs this guard to avoid a false "no data" state. The `!hasRenderableData`
  // check below still hides the loading overlay whenever real lines are visible.
  return (
    dataKey != null &&
    !hasRenderableData &&
    (isDataKeyChanging || settlingDataKey === dataKey)
  );
}
